'use client';

import { type PropsWithChildren } from 'react';
import type { SavvySession } from '@auth';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { SessionProvider } from '@lib/auth/client';

// Apollo
import { ApolloWrapper } from '@lib/apollo';

// Utils
import getConfig from '@config';

// Contexts
import { NotificationProvider } from '@contexts/use-notification';
import { LoginAndSignupModalsProvider } from '@contexts/use-login-and-signup-modals';
import { AnalyticsProvider } from '@contexts/use-analytics';
import { ExperimentsProvider } from '@contexts/use-experiments';
import { SessionHandler } from '@components/auth/session-handler';

interface StaticProvidersProps extends PropsWithChildren {
  /**
   * Pre-resolved session from a server layout, or `undefined` to defer
   * resolution to the client. Passing a server-resolved session re-taints the
   * subtree dynamic, so the layouts pass `undefined`. See
   * `docs/prerender-and-the-dynamic-taint.md`. `null` is the explicit
   * "no session" signal used by tests.
   */
  session: SavvySession | null | undefined;
}

/**
 * Client-side providers wrapper for App Router pages.
 * Uses ApolloWrapper from @apollo/client-integration-nextjs for proper
 * SSR streaming and cache hydration with the App Router.
 */
export function StaticProviders({ session, children }: StaticProvidersProps) {
  return (
    <SessionProvider session={session}>
      <GoogleOAuthProvider
        clientId={getConfig().publicRuntimeConfig.google.clientId}
      >
        <ApolloWrapper>
          <NotificationProvider>
            <AnalyticsProvider>
              <ExperimentsProvider>
                <SessionHandler />
                <LoginAndSignupModalsProvider>
                  {children}
                </LoginAndSignupModalsProvider>
              </ExperimentsProvider>
            </AnalyticsProvider>
          </NotificationProvider>
        </ApolloWrapper>
      </GoogleOAuthProvider>
    </SessionProvider>
  );
}
